import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';

/**
 * D-100 — marks an address that several employees use.
 *
 * The wording is the point. "Shared" alone reads as "these people share an
 * account", which is precisely the thing that is NOT true: each person has their
 * own user id, username, mobile, password, roles and audit identity. The label
 * therefore says **mailbox**, and the tooltip says it in full.
 *
 * Deliberately muted rather than a warning colour — a shared mailbox is a
 * legitimate, declared configuration, not a problem to flag.
 */
export function SharedMailboxBadge({
  isShared,
  userCount,
}: {
  isShared: boolean;
  userCount: number | null;
}) {
  if (!isShared) return null;

  const hasCount = typeof userCount === 'number' && userCount > 0;

  return (
    <span
      data-testid="shared-mailbox-badge"
      title={
        hasCount
          ? `Shared mailbox — ${userCount} employees use this address, each with their own login.`
          : 'Shared mailbox — more than one employee uses this address, each with their own login.'
      }
      className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
    >
      <FontAwesomeIcon icon={faUsers} className="h-2.5 w-2.5" />
      Shared mailbox{hasCount ? ` · ${userCount}` : ''}
    </span>
  );
}
